import React, { useEffect, useState } from 'react';
import { Cookie, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { SITE } from '@/constants/testIds';

const STORAGE_KEY = 'e-central:cookie-consent';

const CookieBanner = () => {
  const { t } = useLanguage();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      // small delay so the banner doesn't fight the hero animation
      const id = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(id);
    }
  }, []);

  const decide = (choice) => {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ choice, at: new Date().toISOString() })
    );
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid={SITE.cookieBanner}
      role="dialog"
      aria-label="Cookie consent"
      className="fixed inset-x-3 bottom-3 sm:inset-x-auto sm:right-4 sm:bottom-4 sm:max-w-md z-[60] animate-in fade-in slide-in-from-bottom-4 duration-500"
    >
      <div className="relative bg-[#0B0F0C] text-white rounded-2xl border border-[rgba(34,197,94,0.25)] shadow-[0_30px_60px_-20px_rgba(0,0,0,0.6)] p-5 sm:p-6">
        <button
          type="button"
          aria-label="Close"
          onClick={() => decide('declined')}
          className="absolute top-3 right-3 text-white/50 hover:text-white transition-colors"
        >
          <X size={16} />
        </button>

        <div className="flex items-start gap-3">
          <div className="shrink-0 w-9 h-9 rounded-xl bg-[#22C55E]/15 flex items-center justify-center">
            <Cookie size={18} className="text-[#22C55E]" />
          </div>
          <div className="min-w-0">
            <div className="font-['Outfit'] font-semibold text-base">{t.cookies.title}</div>
            <p className="mt-1.5 text-xs text-white/65 leading-relaxed">{t.cookies.body}</p>

            <div className="mt-4 flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                data-testid={SITE.cookieAccept}
                onClick={() => decide('accepted')}
                className="inline-flex items-center justify-center bg-[#00A651] hover:bg-[#22C55E] text-white text-xs font-semibold py-2.5 px-4 rounded-xl transition-all duration-300 hover:-translate-y-0.5"
              >
                {t.cookies.accept}
              </button>
              <button
                type="button"
                data-testid={SITE.cookieDecline}
                onClick={() => decide('declined')}
                className="inline-flex items-center justify-center bg-transparent border border-white/15 hover:border-[#22C55E]/60 text-white text-xs font-semibold py-2.5 px-4 rounded-xl transition-all duration-300"
              >
                {t.cookies.decline}
              </button>
              <Link
                to="/confidentialite"
                data-testid={SITE.cookieLearnMore}
                onClick={() => setVisible(false)}
                className="inline-flex items-center justify-center text-xs font-medium text-white/65 hover:text-[#22C55E] underline-offset-4 hover:underline transition-colors px-2"
              >
                {t.cookies.learnMore}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
